import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { SystemPageHeader } from "@/ui/molecules/SystemPageHeader";
import { EditUnitFloor } from "./EditUnitFloor";
import { LocationsSectionTabs } from "./LocationsSectionTabs";

type Props = {
	/**
	 * Whether the unit/floor editor sits here, as a header action: only while the page has no locations, since the
	 * toolbar that otherwise carries it isn't shown then.
	 */
	showUnitFloorEditor: boolean;
};

/**
 * The page title under the facility's name, with the Schedule Template's section tabs (Locations selected) right after
 * it on its line, as the Schedule Template's own pages place them, and — while there is no toolbar to hold it — the
 * unit/floor editor on the right. Collapse all sits in the grid's toolbar.
 */
export const LocationsPageHeader: FC<Props> = ({ showUnitFloorEditor }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	// DEFAULT: no eyebrow when the department has no display name — an empty brand line would be a lone dot.
	const eyebrow = chosenDepartment.display_name === "" ? undefined : chosenDepartment.display_name;
	return (
		<SystemPageHeader
			eyebrow={eyebrow}
			title={t("locations.title")}
			titleComponent="h1"
			// The grid is what this page is for, so the header stays the app's standard size and the section tabs share
			// the title's line, right after it (as on the Schedule Template), instead of taking a row of their own.
			titleSize="section"
			titleAdornment={<LocationsSectionTabs />}
			// Self-gated: shown only where units and floors aren't read from an EMR.
			actions={showUnitFloorEditor ? <EditUnitFloor size="small" popoverPosition="left" iconOnly={false} /> : undefined}
			dataTestId="locations-page-header"
		/>
	);
};
